import type { AppliedFlashRoomRate } from '../types';

import { getRoomAssetNameByPackageId } from './roomMapping';
import type { RelatedPackageRoom } from './roomMapping';

type FlashRoomRateAssignment = {
  packageData: Record<string, unknown>;
  packageId: string;
};

export type UnmatchedFlashPackage = {
  packageId?: string;
  packageName: string;
  roomAssetName?: string;
};

function getUnmatchedReason(
  packageId: string | undefined,
  mappedRoomType: Pick<App.RoomType, 'name'> | undefined,
  roomRateCount: number,
) {
  if (!packageId) {
    return 'missing a Salesforce id';
  }

  if (!mappedRoomType) {
    return 'no room chosen';
  }

  if (roomRateCount > 1) {
    return `"${mappedRoomType.name}" has several rate plans and none is chosen`;
  }

  return `"${mappedRoomType.name}" has no applied rate`;
}

/** The id the wizard keys a package by: its Salesforce id, or its uuid for a package that has none. */
export function getPackageSalesforceId(offerPackage: App.Package) {
  const salesforceId = offerPackage.id_salesforce_external ?? offerPackage.le_package_id;

  return salesforceId ? String(salesforceId) : undefined;
}

/** The package's own uuid, which the status route needs; the other package routes take either id. */
export function getPackageUuid(offerPackage: App.Package) {
  return ('le_package_id' in offerPackage && offerPackage.le_package_id) || undefined;
}

export function getOfferPackageName(offerPackage: App.Package) {
  return (
    offerPackage.deal_option_name || offerPackage.name || getPackageSalesforceId(offerPackage) || 'Unknown package'
  );
}

export function getPackageNumberOfNights(offerPackage: App.Package) {
  return 'number_of_nights' in offerPackage && typeof offerPackage.number_of_nights === 'number'
    ? offerPackage.number_of_nights
    : undefined;
}

export function sortPackagesByLengthOfStay(packages: Array<App.Package>) {
  return [...packages].sort((a, b) => {
    const nightsA = getPackageNumberOfNights(a) ?? Number.POSITIVE_INFINITY;
    const nightsB = getPackageNumberOfNights(b) ?? Number.POSITIVE_INFINITY;

    return nightsA - nightsB || getOfferPackageName(a).localeCompare(getOfferPackageName(b));
  });
}

// ApiError's own `message` is often a generic "Validation error" — the field-specific reason
// (e.g. "A flexible nights package requires a max extra nights value") lives in `errors`.
export function getPackageUpdateErrorDetail(error: unknown, fallbackMessage: string) {
  if (
    error &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray(error.errors) &&
    error.errors.length > 0
  ) {
    return error.errors
      .map((issue) => (typeof issue === 'string' ? issue : issue?.message))
      .filter(Boolean)
      .join('; ');
  }

  return fallbackMessage;
}

export function buildFlashPackageRoomRateAssignments({
  offerPackages = [],
  propertyId,
  ratePlanIdByPackageId = {},
  relatedPackageRooms = [],
  roomRates,
  roomTypeIdByPackageId = {},
  roomTypes,
}: {
  offerPackages?: Array<App.Package>;
  propertyId: string;
  /** The rate plan chosen for a package; only needed when its room has more than one. */
  ratePlanIdByPackageId?: Record<string, string>;
  relatedPackageRooms?: Array<RelatedPackageRoom>;
  roomRates: Array<AppliedFlashRoomRate>;
  roomTypeIdByPackageId?: Record<string, string>;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
}): {
  assignableRoomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  assignments: Array<FlashRoomRateAssignment>;
  unmatchedPackageNames: Array<string>;
  unmatchedPackages: Array<UnmatchedFlashPackage>;
} {
  const roomRatesByRoomTypeId = new Map<string, Array<AppliedFlashRoomRate>>();

  roomRates.forEach((roomRate) => {
    const existingRoomRates = roomRatesByRoomTypeId.get(roomRate.roomTypeId) ?? [];
    roomRatesByRoomTypeId.set(roomRate.roomTypeId, existingRoomRates.concat(roomRate));
  });

  const roomsWithRates = roomTypes.filter((roomType) => roomRatesByRoomTypeId.has(roomType.id));
  const roomAssetNameByPackageId = getRoomAssetNameByPackageId(relatedPackageRooms);

  const assignments: Array<FlashRoomRateAssignment> = [];
  const unmatchedPackageNames: Array<string> = [];
  const unmatchedPackages: Array<UnmatchedFlashPackage> = [];

  offerPackages.forEach((offerPackage) => {
    const packageId = getPackageSalesforceId(offerPackage);
    const roomAssetName = packageId ? roomAssetNameByPackageId[packageId] : undefined;
    // The mapping is the CM-confirmed room; there's no name-matching fallback.
    const mappedRoomTypeId = packageId ? roomTypeIdByPackageId[packageId] : undefined;
    const mappedRoomType = mappedRoomTypeId
      ? roomTypes.find((candidate) => candidate.id === mappedRoomTypeId)
      : undefined;
    const roomType = roomsWithRates.find((candidate) => candidate.id === mappedRoomType?.id);
    const roomRatesForRoom = roomType ? (roomRatesByRoomTypeId.get(roomType.id) ?? []) : [];
    const chosenRatePlanId = packageId ? ratePlanIdByPackageId[packageId] : undefined;
    // A room rate is one room with one rate plan, so a room with several needs the package's pick.
    const roomRate =
      roomRatesForRoom.length === 1
        ? roomRatesForRoom[0]
        : roomRatesForRoom.find((candidate) => candidate.ratePlanId === chosenRatePlanId);

    if (!packageId || !roomType || !roomRate) {
      const reason = getUnmatchedReason(packageId, mappedRoomType, roomRatesForRoom.length);

      unmatchedPackageNames.push(`${getOfferPackageName(offerPackage)} (${reason})`);
      unmatchedPackages.push({ packageId, packageName: getOfferPackageName(offerPackage), roomAssetName });
      return;
    }

    assignments.push({
      packageId,
      packageData: {
        fk_property_id: propertyId,
        fk_room_type_id: roomType.id,
        fk_room_rate_id: roomRate.roomRateId,
      },
    });
  });

  return { assignableRoomTypes: roomsWithRates, assignments, unmatchedPackageNames, unmatchedPackages };
}

export function getMaxExtraNights(pkg: App.Package): number {
  return 'max_extra_nights' in pkg && typeof pkg.max_extra_nights === 'number' ? pkg.max_extra_nights : 0;
}
